///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { FormControl, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { GtthAlertService } from '../../../services/alert.service';
import { GtthEventManager } from '../../../services/event-manager.service';
import {
    CustomerIdWithNameProjection,
    EngineProcessTypeEnum,
    ProjectDTO,
    ProjectFolderDTO,
    ProjectFolderEnum
} from '../../../generated/shared-types.model';
import { Observable, Subject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { ProjectMoveService } from '../../../services/project/project-move.service';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { moveProject } from '../store/project-modal-management/project-modal-management.actions';
import { Store } from '@ngrx/store';
import { FoldersTreeService, FolderTreeNode } from '../../folders-tree/folders-tree.service';
import { FoldersService, RootFoldersInfo } from '../../../services/project/folders.service';
import { ProjectService } from '../../../services/project/project.service';
import { isDefined } from '@siklu/sui-lib/utils';
import { SuiLoadingDirective, SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { GtthSharedCommonModule } from '../../../shared/shared-common.module';
import { FoldersTreeComponent } from '../../folders-tree/folders-tree.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';
import { MatSelectModule } from '@angular/material/select';
import { GtthSharePipesModule } from '../../../shared/shared-pipes.module';

@Component({
    selector: 'winde-project-move-dialog',
    templateUrl: './project-move-dialog.component.html',
    styleUrls: ['../../projects.scss'],
    providers: [FoldersTreeService],
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        GtthSharedCommonModule,
        GtthSharePipesModule,
        ButtonsDirectivesModule,
        SuiLoadingDirective,
        SuiTranslatePipe,
        SuiSvgIconComponent,
        FoldersTreeComponent
    ]
})
export class ProjectMoveDialogComponent implements OnInit, OnDestroy {
    @ViewChild('editForm', { static: true }) editForm: NgForm;

    public $companies: Observable<CustomerIdWithNameProjection[]>;

    public companyControl = new FormControl<CustomerIdWithNameProjection>(undefined, Validators.required);
    public selectedCustomer: CustomerIdWithNameProjection;
    public projectMoveDto: ProjectDTO;
    public isSaveStarted = false;
    public isBomConfigProject = false;
    public showFormValidationAlert: boolean;
    public alertMessageSubject: Subject<string>;
    public showConfirmation: boolean;
    public moveValidation: boolean;
    public loaderKey = LoaderIds.PROJECT_MOVE_DIALOG_PRELOADER_ID;
    public tree: FolderTreeNode[];
    public rootFolderIds: RootFoldersInfo;
    private parentFolderId: number;

    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly store: Store,
        private readonly alertService: GtthAlertService,
        private readonly eventManager: GtthEventManager,
        private readonly projectMoveService: ProjectMoveService,
        private readonly projectFolderService: FoldersService,
        private readonly foldersTreeService: FoldersTreeService,
        public readonly preloaderService: PreloaderService,
        public readonly dialogRef: MatDialogRef<ProjectMoveDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { project: ProjectDTO }
    ) {
        this.alertMessageSubject = new Subject();
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.submitForm();
        }
    }

    ngOnInit() {
        this.preloaderService.show(this.loaderKey);
        this.projectMoveDto = this.data.project;
        this.isSaveStarted = false;
        this.isBomConfigProject = this.projectMoveDto.engineType === EngineProcessTypeEnum.BOM_CONFIGURATOR;

        this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedCompany: CustomerIdWithNameProjection) => {
            this.selectedCustomer = selectedCompany;
            this.getFoldersByCustomerId();
        });

        this.foldersTreeService.selectionChanges$.pipe(takeUntil(this.destroy$)).subscribe((folderId: number) => {
            this.parentFolderId = folderId;
        });

        this.loadCompaniesList();
    }

    ngOnDestroy(): void {
        if (this.alertMessageSubject) {
            this.alertMessageSubject.complete();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    public submitForm() {
        if (this.projectMoveDto.customerId === this.selectedCustomer.id) {
            this.moveInside();
        } else if (!this.moveValidation) {
            this.validateMove();
        } else {
            this.confirmMove();
        }
    }

    private loadCompaniesList() {
        this.$companies = this.projectMoveService.getCompaniesToMove(this.projectMoveDto.id);

        this.$companies = this.$companies.pipe(
            map((companies: CustomerIdWithNameProjection[]) => {
                const currentCustomer = companies.find((item) => item.id === this.projectMoveDto.customerId);

                if (currentCustomer) {
                    currentCustomer.name = `${currentCustomer.name} (current company)`;
                }
                this.selectedCustomer = currentCustomer;
                this.companyControl.setValue(currentCustomer);

                return companies;
            })
        );
    }

    private getFoldersByCustomerId() {
        this.tree = [];
        this.rootFolderIds = {};
        this.projectFolderService
            .getFolders(this.selectedCustomer.id, this.isBomConfigProject ? ProjectFolderEnum.BOM_CONFIG : ProjectFolderEnum.PROJECT)
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((folders: ProjectFolderDTO[]) => {
                this.foldersTreeService.createTree(folders);
                this.foldersTreeService.selectRootFolder();
                this.rootFolderIds = this.projectFolderService.getRootFolderIds(this.selectedCustomer.id);
            });
    }

    private defineParentFolder(): void {
        if (!isDefined(this.parentFolderId)) {
            this.parentFolderId = this.isBomConfigProject
                ? this.rootFolderIds[ProjectFolderEnum.BOM_CONFIG]
                : this.rootFolderIds[ProjectFolderEnum.PROJECT];
        }
    }

    private validateMove(): void {
        if (this.editForm.form.invalid || !this.selectedCustomer) {
            this.alertMessageSubject.next('error.formRequiredFields');
            return;
        }
        this.alertMessageSubject.next('');
        this.defineParentFolder();

        this.preloaderService.show(this.loaderKey);

        this.projectMoveService
            .validateMoveProject(this.projectMoveDto.id, this.selectedCustomer.id)
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((res: string) => {
                if (res) {
                    this.alertMessageSubject.next(res);
                    this.moveValidation = false;
                    return;
                }
                this.moveValidation = true;
                this.showConfirmation = true;
            });
    }

    private moveInside(): void {
        if (!this.isSaveStarted) {
            this.preloaderService.show(this.loaderKey);

            this.isSaveStarted = true;
            this.defineParentFolder();

            this.projectMoveService
                .moveProject(this.projectMoveDto.id, this.projectMoveDto.customerId, this.parentFolderId)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe({
                    next: (res: boolean) => this.onSaveSuccess(res),
                    error: (res: Response) => this.onSaveError(res)
                });
        }
    }

    private confirmMove(): void {
        if (!this.isSaveStarted) {
            this.preloaderService.show(this.loaderKey);
            this.isSaveStarted = true;
            this.projectMoveService
                .moveProject(this.projectMoveDto.id, this.selectedCustomer.id, this.parentFolderId)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe({
                    next: (res: boolean) => this.onSaveSuccess(res),
                    error: (res: Response) => this.onSaveError(res)
                });
        }
    }

    private onSaveSuccess(res) {
        this.isSaveStarted = false;

        this.store.dispatch(moveProject({ targetCustomerId: this.selectedCustomer.id }));
        this.eventManager.broadcast({ name: 'projectModified', content: 'OK' });
        if (res) {
            this.dialogRef.close();
        }
    }

    private onSaveError(error) {
        try {
            error.json();
        } catch (exception) {
            error.message = error.text();
            console.log(`Failed to save - ${exception}`);
        }
        this.isSaveStarted = false;
        this.alertService.error(error.message, null, null);
    }
}

@Component({
    selector: 'winde-project-move-popup',
    template: ''
})
export class ProjectGtthMovePopupComponent implements OnInit {
    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,
        private readonly projectService: ProjectService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (isDefined(id)) {
            this.projectService.getProject(id).subscribe((project: ProjectDTO) => {
                if (!project) {
                    this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                } else {
                    const dialogRef = this.dialog.open(ProjectMoveDialogComponent, {
                        width: '800px',
                        position: {
                            top: '30px'
                        },
                        data: {
                            project
                        },
                        closeOnNavigation: true
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                    });
                }
            });
        } else {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        }
    }
}
